import { Box, Button, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ProductCard from "./ProductCard";
import ProductCategoriesBar from "./ProductCategoriesBar";
import CartDrawer from "./CartDrawer";
import useSWR from "swr";
import { generalItemFetcher } from "../../../helpers/Fetchers/fetchers";
import { ICollection } from "../../../models/ICollection";
import { PATH_NAME } from "../../../configs/pathName";
import { ISkuDetail } from "../../../models/ISkuDetail";
import colorConfigs from "../../../configs/colorConfigs";

const ProductCarousel = () => {
  const { data: collections } = useSWR<ICollection[]>(
    PATH_NAME.API_GET_COLLECTION,
    generalItemFetcher
  );

  const sortedCollections = useMemo(
    () => (collections ? [...collections].sort((a, b) => a.priority - b.priority) : []),
    [collections]
  );

  const { data: skus } = useSWR<ISkuDetail[]>(PATH_NAME.API_GET_SKU_DETAILS, generalItemFetcher);

  const [filteredSkus, setFilteredSkus] = useState<ISkuDetail[]>([]);
  const carouselRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const itemsPerView = isMobile ? 1 : 5;
  const totalItems = filteredSkus.length;
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleSelectCollection = (collection: ICollection) => {
    filterSkusByCollection(collection);
  };

  const filterSkusByCollection = useCallback(
    (collection: ICollection) => {
      const filtered = skus?.filter((sku) => sku.collections.includes(collection.id)) ?? [];
      setFilteredSkus(filtered);
      setCurrentIndex(0); // Reset to first page
    },
    [skus]
  );

  useEffect(() => {
    if (collections?.length && skus?.length) {
      filterSkusByCollection(collections[0]);
    }
  }, [collections, skus, filterSkusByCollection]);

  useEffect(() => {
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.offsetWidth / itemsPerView;
      carouselRef.current.scrollLeft = currentIndex * itemWidth;
      console.log("currentIndex * itemWidth",currentIndex * itemWidth)
    }
  }, [currentIndex, itemsPerView]);

  const nextSlide = () => {
    if (currentIndex + itemsPerView < totalItems) {
      setCurrentIndex((prev) => Math.min(prev + 1, totalItems - itemsPerView));
    }
  };
  
  const prevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Box sx={{ overflow: "hidden", height: "100%", width: "100%" }}>
      <ProductCategoriesBar collections={sortedCollections ?? []} onSelectCollection={handleSelectCollection} />

      <Stack direction="row" spacing={2} sx={{ width: "100%", alignItems: "center", padding: "1rem" }}>
        <Button 
          sx={{ color: colorConfigs.primary }}
          onClick={prevSlide}
          disabled={currentIndex === 0} // Disable when at first item
        >
          <ArrowBackIosNewIcon />
        </Button>

        <Box
          ref={carouselRef}
          sx={{
            display: "grid",
            gridAutoFlow: "column",
            gridAutoColumns: `calc(100% / ${itemsPerView})`,
            width: "100%",
            overflow: "hidden",
            scrollBehavior: "smooth",
            scrollSnapType: "x mandatory",
            paddingY: "1rem",
            "&::-webkit-scrollbar": { display: "none" },
            justifyContent: "center",
          }}
        >
          {filteredSkus.slice(currentIndex, currentIndex + itemsPerView).map((sku, index) => (
            <ProductCard key={sku.id} addCartAction={() => {}} sku={sku} />
          ))}
        </Box>

        <Button 
          sx={{ color: colorConfigs.primary }}
          onClick={nextSlide}
          disabled={currentIndex + itemsPerView >= totalItems} // Disable when at last items
        >
          <ArrowForwardIosIcon />
        </Button>
      </Stack>
    </Box>
  );
};

export default ProductCarousel;
